import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import { useLucideIcon } from "../../Icons/lucide-react/index";
//@ts-ignore
import { tw } from "@careinfo/tailwind";
import usePostHook from "../../hooks/usePostHook";
import usePostFormDataHook from "../../hooks/usePostFormDataHook"; // your hook
import { useCallback, useEffect } from "react";
import APIS from "../../constant/Endpoint";
import { EditorButton } from "../shared/EditorButton";

export default function SignatureEditor({
  value,
  username,
}: {
  value: string;
  username: string;
}) {
  const {
    List,
    ListOrdered,
    TextBold,
    TextItalic,
    TextUnderline,
    Image: ImageIcon,
  } = useLucideIcon();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Image.configure({
        inline: true,
        allowBase64: false, // disable base64
        resize: {
          enabled: true,
          alwaysPreserveAspectRatio: true,
        },
      }),
    ],
    editorProps: {
      attributes: {
        class: "pt-2 min-h-[400px] focus:outline-none border-none",
      },
    },
    content: value || "<p></p>", // initial empty content
    shouldRerenderOnTransaction: true,
  });

  // Load existing HTML content
  // useEffect(() => {
  //   if (editor && value) {
  //     editor.commands.setContent(value);
  //   }
  // }, [editor, value]);

  const { mutateAsync: postSignature, isPostLoading: isSignaturePostLoading } =
    usePostHook({ queryKey: ["signature"] });

  const { mutateAsync: uploadFileMutation, isPostLoading: isFileUploading } =
    usePostFormDataHook({ queryKey: ["upload-file"] });

  // Save signature
  const saveSignature = async () => {
    if (!editor) return;
    const url = `${APIS.EMAIL}signature/`;
    const formData = { signature: editor.getHTML(), username };

    try {
      const res = await postSignature({ url, formData });
      if (res?.status === 200) {
        console.log("Signature saved successfully!");
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Upload image to S3 and insert URL
  const handleFileSubmit = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0 && editor) {
        const file = files[0];

        const formData = new FormData();
        formData.append("file", file);
        try {
          const res = await uploadFileMutation({
            url: APIS.UPLOAD_FILE, // your DRF S3 endpoint
            formData,
          });

          if (res?.data) {
            editor.chain().focus().setImage({ src: res?.data?.url }).run();
          }
        } catch (err) {
          console.error("Image upload failed:", err);
        } finally {
          e.target.value = ""; // reset input
        }
      }
    },
    [editor, uploadFileMutation]
  );

  if (!editor) return null;

  return (
    <>
      <div style={{ padding: 10 }}>
        <EditorContent editor={editor} />
        <div className={tw`flex gap-2 flex-wrap items-center`}>
          <label
            title="Upload image"
            className={tw`flex items-center cursor-pointer rounded-md border transition font-medium hover:text-red-400 p-2`}
          >
            <ImageIcon size={20} />
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSubmit}
              aria-label="Upload image"
            />
          </label>

          {isFileUploading && (
            <span className={tw`text-sm text-gray-500`}>Uploading...</span>
          )}

          <EditorButton
            title="Bold (Ctrl+B)"
            isActive={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
            icon={<TextBold />}
          />
          <EditorButton
            title="Italic (Ctrl+I)"
            isActive={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            icon={<TextItalic />}
          />
          <EditorButton
            title="Underline (Ctrl+U)"
            isActive={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            icon={<TextUnderline />}
          />
          <EditorButton
            title="Numbered list (Ctrl+Shift+7)"
            isActive={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            icon={<ListOrdered />}
          />
          <EditorButton
            title="Bulleted list (Ctrl+Shift+8)"
            isActive={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            icon={<List />}
          />
        </div>
      </div>

      <div className={tw`p-3 border-t border-gray-200`}>
        <button
          disabled={isSignaturePostLoading}
          className={tw`px-6 py-2.5 rounded-lg font-medium transition-all focus:outline-none bg-red-500 text-white shadow-md`}
          onClick={saveSignature}
        >
          {isSignaturePostLoading ? "Saving..." : "Save Signature"}
        </button>
      </div>
    </>
  );
}
